import { Button, CircularProgress, Divider, Paper, Typography } from '@mui/material';
import { Box } from '@mui/system';
import { GridColDef } from '@mui/x-data-grid';
import { Form, Formik } from 'formik';
import moment from 'moment';
import React from 'react';
import AccordionControlled from '../../../components/AccordionControlled/AccordionControlled';
import Details from '../../../components/Details/Details';
import FormController from '../../../components/form_v2/highLevelComponents/FormController';
import FormActionButtonLayout from '../../../components/layout/Form/FormActionButtonLayout';
import FormLayout from '../../../components/layout/Form/FormLayout';
import FilterComponentV2 from '../../../components/TablegridWithFilters/FilterComponent/FilterComponentv2';
import TabsLayout from '../../../components/Tabs/TabsLayout';
import { afeIssue, afeValidation, afeVersion } from '../../../lib/fetchers';
import { downloadBase64PDF, eliminateOjectNotTruthyValues, isSubmitEnabled, matchRefsWithData } from '../../../lib/helperFuncs';
import { afeIssueOptions, afeIssueRefs, afeValidationRefs } from '../../../lib/static/details/staticDetails';
import { afeIssueFields, afeValidationFields } from '../../../lib/static/filters/filterFields';
import { afeIssueValidationInitialValues, afeValidationInitialValues } from '../../../lib/static/filters/initialValues';
import { afeIssueValidationSchema, afeValidationSchema } from '../../../lib/static/filters/ValidationSchema';
import HttpService from '../../../services/HttpService';
import Theme from '../../../Themes/main.theme';
import { InteroperabilityPermissions } from '../../../lib/permissions';
import UserService from '../../../services/UserService';

const AfeBoard = () => {
  const [data, setData] = React.useState<any>();
  const [afeEmployeeAfm, setAfeEmployeeAfm] = React.useState<any>();
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>();
  const [tab, setTab] = React.useState(0);

  const item_columns: GridColDef[] = [
    {
      field: 'crfDoy',
      headerName: 'Δ.Ο.Υ.',
      minWidth: 150,

    },
    {
      field: 'crfAa',
      headerName: 'ΑΑ',
      minWidth: 150,

    },
    {
      field: 'crfDate',
      headerName: 'Ημερομηνία',
      flex: 1,
      minWidth: 170,
      renderCell: (params) => params.row.crfDate
        ? moment(params.row.crfDate).format('DD/MM/YYYY').toLocaleString()
        : '-'
    },
    {
      field: 'crfAmnt',
      headerName: 'Ποσό',
      flex: 1,
      minWidth: 170
    },
    {
      field: 'crfTxtDescr',
      headerName: 'Είδος Φόρου',
      flex: 1,
      minWidth: 170
    },
  ];

  const onReset = (formik: any) => {   
    Object.keys(formik.values).map((value) => {
      formik.setFieldValue(value, afeIssueValidationInitialValues[value]);
    });
  };

  const onSubmitValidation = async (values: any) => {
    setIsLoading(true);    
    if (values.rvAfeType === 1 && !values.rvAfeDoy) {      
      setError('Πρέπει να συμπληρώσετε κωδικό Δ.Ο.Υ.');
      setIsLoading(false);
      return;
    }  
    try {
      const response = await afeValidation(eliminateOjectNotTruthyValues(values));
      setError('');      
      setData({
        ...response.data,
        rvAfeFound: response.data.rvAfeFound === '0'
          ? 'Δεν Βρέθηκε'
          : 'Βρέθηκε',
        rvAfeReason: afeIssueOptions.find(x => x.value === response.data.rvAfeReason)?.key,
        rvAfeIssueDate: moment(response.data.rvAfeIssueDate).format('DD/MM/YYYY').toLocaleString(),
        rvAfeEndDate: moment(response.data.rvAfeEndDate).format('DD/MM/YYYY').toLocaleString(),
        rvAfiExpirationDate: moment(response.data.rvAfiExpirationDate).format('DD/MM/YYYY').toLocaleString(),
        rvAfiGeeNoteDate: moment(response.data.rvAfiGeeNoteDate).format('DD/MM/YYYY').toLocaleString(),
      });
    } catch (error: any) {
      console.error(error);
      if (error.response.status === 400) {
        setError(error.response.data.message);
      }
    }
    setIsLoading(false);
  };

  const onSubmitVersion = async () => {
    setIsLoading(true);
    try {
      const response = await afeVersion();      
      setError('');      
      setData(response.data);
    } catch (error: any) {
      console.error(error);
      if (error.response.status === 400) {
        setError(error.response.data.message);
      }
    }
    setIsLoading(false);
  };
  const onSubmitIssue = async (values: any) => {
    setIsLoading(true);
    try {
      const response = await afeIssue(eliminateOjectNotTruthyValues(values));
      setError('');      
      setData({
        ...response.data,
        rvAfeIssueDate: moment(response.data.rvAfeIssueDate).format('DD/MM/YYYY').toLocaleString(),
        rvAfeEndDate: moment(response.data.rvAfeEndDate).format('DD/MM/YYYY').toLocaleString(),
        rvAfeReason: afeIssueOptions.find(x => x.value === response.data.rvAfeReason)?.key,
        rvAfeINiFlag: response.data.rvAfeINiFlag === '1'
          ? 'Φυσικό Πρόσωπο'
          : 'μη Φυσικό Πρόσωπο'
      });
    } catch (error: any) {
      console.error(error);
      if (error.response.status === 400) {
        setError(error.response.data.message);
      }
    }
    setIsLoading(false);
  };

  const getAfeEmployeeAfm = async () => {
    try {
      const response = await HttpService.getAxiosClient().get(`${process.env.REACT_APP_BACKEND_URL}/users/user`);
      setAfeEmployeeAfm(response.data.afm);
    } catch (error) {
      console.error(error);
    }
  };

  React.useEffect(() => {
    getAfeEmployeeAfm();
  }, []);

  const detailsValidation = matchRefsWithData(data, afeValidationRefs);
  const detailsIssue = matchRefsWithData(data, afeIssueRefs);

  const tabLabels = [
    ...(UserService.hasRole([InteroperabilityPermissions.PermissionsInternalAfeIssue])
      ? [{
        label: <Typography variant='h4'>Έκδοση/Έλεγχος Εγκυρότητας ΑΦΕ</Typography>,
      }]
      : []
    ),
    ...(UserService.hasRole([InteroperabilityPermissions.PermissionsInternalAfeValidation])
      ? [{
        label: <Typography variant='h4'>Εγκυρότητα ενός ΑΦΕ</Typography>,
      }]
      : []),
    ...(UserService.hasRole([InteroperabilityPermissions.PermissionsInternalAfeVersion])
      ? [{
        label: <Typography variant='h4'>Αριθμός Έκδοσης ΑΦΕ</Typography>,
      }]
      : [])
  ];

  const tab1 = <Box my={4}>
    <ul>
      <li>
        Τα πεδία <span style={{fontWeight: 'bold'}}> Περιγραφή της Υπηρεσίας που αιτείται την έκδοση του ΑΦΕ </span> 
        και <span style={{fontWeight: 'bold'}}>Ταχυδρομική διεύθυνση της Υπηρεσίας που 
          αιτείται την έκδοση του</span> είναι προεραιτικά
      </li>
      <li>
        Σε περίπτωση συμπλήρωσης των πεδίων <span style={{textDecoration: 'underline'}}> Περιγραφή της Υπηρεσίας που αιτείται την έκδοση του ΑΦΕ </span> 
        και <span style={{textDecoration: 'underline'}}>Ταχυδρομική διεύθυνση της Υπηρεσίας που 
          αιτείται την έκδοση του</span> συμπληρώστε τα <span style={{fontWeight: 'bold'}}> ΜΟΝΟ ΣΤΑ ΚΕΦΑΛΑΙΑ</span> 
      </li>
    </ul>
    {afeEmployeeAfm
      ? <Paper sx={{padding: 2}} elevation={3}>
        <Formik
          enableReinitialize
          onSubmit={() => undefined}
          initialValues={{...afeIssueValidationInitialValues, afeEmployeeAfm: afeEmployeeAfm}}
          validationSchema={afeIssueValidationSchema}
          validateOnBlur={true}
          validateOnMount
        >
          {formik =>
            (<div>
              <Form>
                <FormLayout>
                  {afeIssueFields.map((el: any) => {
                    if (Array.isArray(el)) {
                      return <Box display='flex'
                        gap={3}
                        key={el + 'Box'}>
                        {el.map((subEl) => {
                          return (
                            <div style={{width: '100%'}} 
                              key={subEl.name}
                            >
                              <FormController 
                                {...subEl}
                                formik={formik}
                              />
                            </div>
                          );
                        })}
                      </Box>;
                    }
                    return (
                      <FormController 
                        key={el.name} 
                        formik={formik}
                        {...el}
                      />); 
                  })}
                </FormLayout>
                <FormActionButtonLayout>
                  <Button variant='outlined' color='primary'
                    onClick={() => onReset(formik)}
                    sx={{ borderRadius: 24, maxWidth: '150px', maxHeight: '50px', minWidth: '150px', minHeight: '50px'}}
                  > Καθαρισμός </Button>
                  <Button variant='contained'
                    disabled={isSubmitEnabled(formik?.errors, formik?.dirty)}
                    onClick={() => onSubmitIssue(formik.values)}
                    sx={{ borderRadius: 24, maxWidth: '150px', maxHeight: '50px', minWidth: '150px', minHeight: '50px'}}
                  > Αναζήτηση</Button>
                </FormActionButtonLayout>
              </Form>
            </div>)
          }
        </Formik>
      </Paper>
      : <Typography variant='h4'>
        Για την αναζήτηση Α.Φ.Ε. απαιτείται να είναι καταχωρημένο το Α.Φ.Μ. σας. 
        Παρακαλούμε επικοινωνήστε με τους διαχειριστές.
      </Typography>}
    <Box mt={5} sx={{ display: 'flex', justifyContent: 'center'}}>
      {isLoading
        ? <CircularProgress />
        : error
          ? <Typography variant='h4'>
            {error}
          </Typography> 
          : data
            ? <Box height='100%'>
              {data?.pdfFile
                ? <Box my={2}>
                  <Button variant='contained' onClick={() => downloadBase64PDF(data?.pdfFile, 'ekdosi_afe')}>Λήψη PDF</Button>
                </Box> 
                : null
              }
              <Details rows={detailsIssue}/>
            </Box>
            : null
      }
    </Box>
  </Box>;
  const tab2 = <Box my={4}>
    <ul>
      <li>
        Όταν ο κωδικός εκδότη ΑΦΕ είναι <span style={{textDecoration: 'underline'}}>INTERNET</span> η συμπλήρωση του κωδικού Δ.Ο.Υ
        <span style={{fontWeight: 'bold'}}> δεν είναι υποχρεωτική</span>
      </li>
    </ul>
    <FilterComponentV2
      title='Φίλτρα Εγκυρότητας ενός ΑΦΕ'
      validationSchema={afeValidationSchema}
      filterFields={afeValidationFields}
      initialValues={afeValidationInitialValues}
      resetFilters={true}
      changeFilters={filterValues => onSubmitValidation(filterValues)}
      validateOnBlur={false}
    />
    <Box mt={5} sx={{ display: 'flex', justifyContent: 'center'}}>
      {isLoading
        ? <CircularProgress />
        : error
          ? <Typography variant='h4'>
            {error}
          </Typography> 
          : data
            ? <Box height='100%'>
              <Details rows={detailsValidation}/>
              <Box mt={2}>
                <AccordionControlled
                  headers={['Επιμεριστικός φόρος κατά ακίνητο - λίστα από εγγραφές']}
                  columns={[item_columns]}
                  rows={[
                    data?.rvAfiEpimeristikos?.item,
                  ]}
                  isLoading={isLoading}/>
              </Box>
            </Box>
            : null
      }
    </Box>
  </Box>;
  const tab3 = <Box my={4} display='flex'
    justifyContent='center'
    mt={5}>
    {
      isLoading
        ? <CircularProgress />
        : <Typography> 
          <span style={{textDecoration: 'underline'}}> Αριθμός Έκδοσης ΑΦΕ:</span>
          <span style={{fontWeight: 'bold'}}> {data}</span> 
        </Typography> 
    }
  </Box>;

  const tabContent = [
    ...(UserService.hasRole([InteroperabilityPermissions.PermissionsInternalAfeValidation])
      ? [tab1]
      : []),
    ...(UserService.hasRole([InteroperabilityPermissions.PermissionsInternalAfeValidation])
      ? [tab2]
      : []),
    ...(UserService.hasRole([InteroperabilityPermissions.PermissionsInternalAfeVersion])
      ? [tab3]
      : [])
  ];

  React.useEffect(() => {
    UserService.hasRole([InteroperabilityPermissions.PermissionsInternalAfeVersion]) && tab === 2 &&
      onSubmitVersion();
  }, [tab]);

  return (
    <>
      <Typography sx={{my: 2}} variant='h2'
        color={Theme?.labelColor?.main}> Αποδεικτικό Φορολογικής Ενημερότητας (ΑΦΕ)</Typography>
      <Divider sx={{ mb: 5, border: 1, color: Theme?.dividerColor?.main }} />
      <TabsLayout
        tab={tab}
        setTab={setTab}
        tabLabels={tabLabels}
        tabContent={tabContent}
        resetFilters={() => {
          setData(undefined);
          setError('');
        }}
      />
    </>
  );
};

export default AfeBoard;